import * as React from 'react'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { IComboBox, MessageBar, MessageBarType, Stack, StackItem } from '@fluentui/react'
import { getDropdownProps } from '../../../../helpers/formHelper'
import LegacyFilePartyDropdown, { IFilePartyDropdownOption } from '../../../Dropdown/LegacyFilePartyDropdown'
import { getFilePartyFromMeta, InvoiceProps } from './InvoiceForm'
import { useFormikContext } from 'formik'
import {
    FilePartyMetaDataDto,
    InvoiceMutationDto,
    InvoiceType,
    MetaDataDto,
} from '../../../../config/finance/invoiceConfig'
import { FilePartyRole } from '../../../../config/filePartyConfig'
import { Link } from 'react-router-dom'
import { useSettings } from '../../../../hooks/useSettings'
import { InvoiceRecipientDto, InvoiceRecipientEnum } from '../../../../context/GlobalContext'
import { useTranslation } from 'react-i18next'

type Props = InvoiceProps & {}

const getFilePartyId = (role: FilePartyRole, meta: MetaDataDto, setting?: InvoiceRecipientDto): string => {
    const fileParties = new Map<FilePartyRole, string[]>([
        [FilePartyRole.Debtor, []],
        [FilePartyRole.Client, []],
    ])

    meta.fileParties
        .filter(fileParty => fileParty.hasAddress && fileParties.has(fileParty.role))
        .forEach(fileParty => fileParties.get(fileParty.role).push(fileParty.id))

    let targetRole = role
    //Switch roles based on the users setting
    if (targetRole === FilePartyRole.Client && setting?.client === InvoiceRecipientEnum.Debtor) {
        targetRole = FilePartyRole.Debtor
    } else if (targetRole === FilePartyRole.Debtor && setting?.debtor === InvoiceRecipientEnum.Client) {
        targetRole = FilePartyRole.Client
    }

    const oppositeRole = targetRole === FilePartyRole.Debtor ? FilePartyRole.Client : FilePartyRole.Debtor
    if (fileParties.get(targetRole).length === 1) {
        return fileParties.get(targetRole)[0]
    } else if (fileParties.get(oppositeRole).length === 1) {
        return fileParties.get(oppositeRole)[0]
    }

    return null
}

export default function InvoiceParties({ invoice, meta, useInitialInvoice }: Props): ReactElement {
    const settings = useSettings()
    const { t: translate } = useTranslation()

    const { errors, values, initialValues, setFieldValue } = useFormikContext<InvoiceMutationDto>()
    const [filePartyOptions, setFilePartyOptions] = useState<IFilePartyDropdownOption<FilePartyMetaDataDto>[]>()
    const filePartyRef = useRef<IComboBox>()
    const isEndUserInvoice = useMemo<boolean>(() => ![InvoiceType.Split].includes(values.type), [values.type])

    useEffect(() => {
        if (isEndUserInvoice) {
            setFilePartyOptions(
                meta.data.fileParties.map((fileParty: FilePartyMetaDataDto) => ({
                    key: fileParty.id,
                    text: fileParty.displayName,
                    disabled: !fileParty.hasAddress,
                    role: fileParty.role,
                    data: fileParty,
                })),
            )
            setFieldValue(
                'clientPivotId',
                (useInitialInvoice && invoice.recipient?.clientId) ||
                    getFilePartyId(FilePartyRole.Client, meta.data, settings?.invoiceRecipient),
            )
            setFieldValue(
                'debtorPivotId',
                (useInitialInvoice && invoice.recipient?.debtorId) ||
                    getFilePartyId(FilePartyRole.Debtor, meta.data, settings?.invoiceRecipient),
            )
        } else {
            setFieldValue('clientPivotId', undefined)
            setFieldValue('debtorPivotId', undefined)
        }
    }, [meta.data, isEndUserInvoice])

    useEffect(() => {
        if (isEndUserInvoice) {
            if (values.debtorPivotId) {
                if (useInitialInvoice && initialValues.debtorPivotId === values.debtorPivotId) {
                    setFieldValue('recipientReference', invoice.recipient?.reference)
                    setFieldValue('recipientEmail', invoice.recipient?.email)
                } else {
                    const recipient = getFilePartyFromMeta(values.debtorPivotId, meta.data.fileParties)
                    setFieldValue('recipientReference', recipient?.reference)
                    setFieldValue('recipientEmail', recipient?.email)
                }
            }
        } else {
            setFieldValue('recipientReference', undefined)
            setFieldValue('recipientEmail', undefined)
        }
    }, [meta.data.fileParties, values.debtorPivotId, isEndUserInvoice])

    if (!meta.loading && meta.data.fileParties.length === 0) {
        return (
            <MessageBar messageBarType={MessageBarType.blocked}>
                Dit dossier bevat geen debiteuren of cli&euml;nten met geldige adresgegevens. Ga naar
                <Link to={`/file/${meta.data.file.id}/party`}>partijen</Link> om dit te verhelpen.
            </MessageBar>
        )
    }

    return (
        <Stack horizontal tokens={{ childrenGap: 'm' }}>
            <StackItem grow={1}>
                <LegacyFilePartyDropdown
                    label={translate('invoice.clientpivotid')}
                    required
                    disabled={meta.loading}
                    options={filePartyOptions}
                    componentRef={filePartyRef}
                    {...getDropdownProps(values.clientPivotId, errors?.clientPivotId, value =>
                        setFieldValue('clientPivotId', value),
                    )}
                />
            </StackItem>
            <StackItem grow={1}>
                <LegacyFilePartyDropdown
                    label={translate('invoice.debtorpivotid')}
                    required
                    disabled={meta.loading}
                    options={filePartyOptions}
                    {...getDropdownProps(values.debtorPivotId, errors?.debtorPivotId, value =>
                        setFieldValue('debtorPivotId', value),
                    )}
                />
            </StackItem>
        </Stack>
    )
}
